import { isDefined, isEmpty } from '@sgme/fp';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { OverlayInjectedProps } from 'react-bootstrap/esm/OverlayTrigger';

import { MaybePrimeBrokenBadge } from '@/App/tabs/MaybePrimeBrokenBadge.tsx';
import { DeleteSharedTabModal } from '@/App/tabs/Tab/components/DeleteSharedTabModal.tsx';
import { TabMenuOverlay } from '@/App/tabs/TabMenu/TabMenuOverlay.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import {
  selectCanDeleteTab,
  selectIsTabActive,
  selectIsTabShared,
  selectTabClientId,
  selectTabName,
  selectTabSharedWithUsers,
} from '@/store/tabs/selectors.ts';
import { changeActiveTab, removeTab, setTabName } from '@/store/tabs/slice.ts';

const UNKNOWN_CLIENT = 'UNKNOWN';

type TabProps = {
  tabId: string;
};

export function Tab({ tabId }: TabProps) {
  const tabRef = useRef<HTMLLIElement>(null);
  const dispatch = useAppDispatch();

  const [doesShowMenu, openMenu, closeMenu] = useBooleanState(false);
  const [doesShowModal, showModal, hideModal] = useBooleanState(false);

  const {
    currentUser: { clients: userClients },
  } = useUserContext();

  const isActive = useAppSelector((state) => selectIsTabActive(state, tabId));
  const isShared = useAppSelector((state) => selectIsTabShared(state, tabId));
  const tabName = useAppSelector((state) => selectTabName(state, tabId));
  const clientId = useAppSelector((state) => selectTabClientId(state, tabId) ?? '');
  const canDelete = useAppSelector(selectCanDeleteTab);

  const clientNameById = userClients.reduce(
    (acc, client) => {
      acc[client.companyId] = {
        companyName: client.companyName,
        primeBroker: client.primeBroker ?? undefined,
      };
      return acc;
    },
    {} as Record<string, { companyName: string; primeBroker: string | undefined }>,
  );
  const clientName = clientNameById[clientId]?.companyName ?? UNKNOWN_CLIENT;
  const primeBroker = clientNameById[clientId]?.primeBroker ?? null;

  const onSelectTab = () => {
    dispatch(changeActiveTab({ tabId }));
  };

  const onChangeName = (newValue: string) => {
    if (isEmpty(newValue)) {
      return;
    }
    dispatch(setTabName({ tabId, tabName: newValue }));
  };

  const onDeleteTab = () => {
    if (isShared) {
      showModal();
    } else {
      dispatch(removeTab(tabId));
    }
  };

  const tabBody = (
    <div className="tab-body d-grid text-truncate">
      <div className="tab-row d-flex flex-row text-truncate">
        <div className="tab-heading flex-x flex-grow-1 gap-1 text-truncate">
          {isShared && (
            <i className="quote-status-ico icon icon-sm text-secondary lh-1" data-e2e="tab-shared-icon">
              group
            </i>
          )}
          {isActive ? (
            <TabNameEditor tabName={tabName} onChangeName={onChangeName} />
          ) : (
            <ReadonlyTabName tabName={tabName} />
          )}
        </div>
        <SettingsButton onClick={isActive ? openMenu : undefined} />
        {canDelete && <DeleteButton onClick={onDeleteTab} />}
      </div>
      <div className="tab-row d-flex flex-row align-items-center gap-1" onClick={isActive ? openMenu : undefined}>
        <span className="fw-normal text-secondary fs-6 lh-1 text-truncate" data-e2e="client-name">
          {clientName}
        </span>
        <MaybePrimeBrokenBadge primeBroker={primeBroker} />
      </div>
    </div>
  );

  return (
    <>
      <li ref={tabRef} className="nav-item flex-fill" data-e2e="workspace-tab">
        <OverlayTrigger placement="bottom" overlay={<SharedWithTooltip tabId={tabId} />} container={tabRef.current}>
          {isActive ? (
            <span className="tab-item tab-item-lg nav-link w-100 active bg-lvl4">{tabBody}</span>
          ) : (
            // oxlint-disable-next-line anchor-is-valid -- matches the SG Bootstrap nav-tabs markup (ul > li > a.nav-link), not a real page link
            <a
              href="#"
              className="tab-item tab-item-lg nav-link w-100"
              onClick={(event) => {
                event.preventDefault();
                onSelectTab();
              }}
            >
              {tabBody}
            </a>
          )}
        </OverlayTrigger>
      </li>
      {doesShowMenu && <TabMenuOverlay target={tabRef.current} tabId={tabId} closeMenu={closeMenu} />}
      <DeleteSharedTabModal tabId={tabId} show={doesShowModal} close={hideModal} />
    </>
  );
}

//////////////////////////
// SECONDARY COMPONENTS //
//////////////////////////

function SharedWithTooltip({ tabId, ...props }: OverlayInjectedProps & { tabId: string }) {
  const sharedWithUsers = useAppSelector((state) => selectTabSharedWithUsers(state, tabId));

  if (sharedWithUsers.length === 0) {
    return null;
  }

  return <Tooltip {...props}>{sharedWithUsers.join(' ')}</Tooltip>;
}

function DeleteButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="tab-close-btn btn btn-sm btn-icon btn-flat-secondary"
      onClick={onClick}
      data-e2e="delete-tab-btn"
    >
      <i className="icon">close</i>
    </button>
  );
}

function SettingsButton({ onClick }: { onClick: (() => void) | undefined }) {
  return (
    <button
      type="button"
      className="tab-setting-btn btn btn-sm btn-icon btn-flat-secondary"
      onClick={onClick}
      data-e2e="tab-menu-button"
    >
      <i className="icon">tune</i>
    </button>
  );
}

export function TabNameEditor({ tabName, onChangeName }: { tabName: string; onChangeName: (value: string) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onClick = () => setIsEditing(true);
  const onBlur = () => {
    setIsEditing(false);
    const newTabname = inputRef?.current?.value;
    if (isDefined(newTabname) && newTabname.length > 0 && newTabname !== tabName) {
      onChangeName(newTabname);
    }
  };

  useEffect(() => {
    if (isEditing) {
      const inputEl = inputRef.current;
      if (inputEl !== null) {
        inputEl.value = tabName;
      }
    }
  }, [isEditing, tabName]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Escape') {
      inputRef?.current?.blur();
    }
  };

  if (isEditing) {
    return (
      <input
        // oxlint-disable-next-line no-autofocus -- behavior required
        autoFocus
        ref={inputRef}
        className="form-control"
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        data-e2e="tab-name-input"
      />
    );
  }

  return <ReadonlyTabName tabName={tabName} onClick={onClick} />;
}

function ReadonlyTabName({ tabName, onClick }: { tabName: string; onClick?: () => void }) {
  return (
    <p className="text-truncate mb-0" onClick={onClick} data-e2e="readonly-tab-name">
      {tabName}
    </p>
  );
}

/**
 * create a boolean state, and expose functions to manage it.
 * @exemple const [doesShowMenu, showMenu, hideMenu] = useBooleanState(false);
 * @param initialValue the initial value of the state
 * @returns [value, setToTrue, setToFalse, toggle]
 */
function useBooleanState(initialValue: boolean) {
  const [value, setValue] = useState(initialValue);

  const setToTrue = () => setValue(true);
  const setToFalse = () => setValue(false);
  const toggle = () => setValue((prev) => !prev);

  return [value, setToTrue, setToFalse, toggle] as const;
}
